import { useState, useMemo, useCallback } from 'react';
import { useAuth } from '../Auth';
import { normalizeFilesOnUpload } from '../../lib/fileUtils';
import { useImageOptimizer } from './useImageOptimizer';
import API from '../../API';

export const useFileUpload = ({
    type,
    acceptableFileTypes,
    fileHandleType = 'qr_files',
    scheduleId='',
}) => {
    const [error, setError] = useState('');
    const { dataDispatch, qr_data, optionsDispatch, fileHandler, activeOrgId } = useAuth();
    const { files, setFiles, setLogo } = fileHandler;
    const processFile = useImageOptimizer();

    const filesToDisplay = useMemo(() => {
        if (fileHandleType === 'schedule') {
            if (!scheduleId) {
                console.error(new Error('missing schedule id'));
                return;
            };

            return qr_data.data?.schedule[scheduleId]?.resources?.data || [];
        };

        return qr_data.data?.[type]?.files || [];
    
    }, [fileHandleType, scheduleId, qr_data.data?.schedule?.[scheduleId]?.resources?.data, qr_data.data?.[type]?.files]);

    const onDeleteFile = (id) => {
        setError('');
        const getFiles = () => {
            if (fileHandleType === 'schedule') {
                if (!scheduleId) {
                    const scheduleError = new Error("Error deleting schedule file");
                    console.error(scheduleError);
                    setError(scheduleError.message);
                };

                return qr_data.data.schedule?.[scheduleId]?.resources?.data;
            };
            return qr_data.data?.[type]?.files || [];
        };

        const resourceFiles = getFiles();
        const fileToDelete = resourceFiles.find((file) => file.id === id);
        if (!fileToDelete) {
            return;
        };

        const filteredFiles = resourceFiles.filter(f => f.id !== id);
        if (!files.deleteFiles?.includes(fileToDelete.path) && fileToDelete.path?.includes('qr_files')) {
            setFiles(prev => ({ ...prev, deleteFiles: [ ...prev.deleteFiles, fileToDelete.path] }));
        };

        if (fileToDelete.path && files.qrFiles.includes(fileToDelete.path)) {
            setFiles(prev => ({ ...prev, qrFiles: prev.qrFiles.filter(path => path !== fileToDelete.path) }));
        };

        if (fileHandleType === 'schedule') {
            dataDispatch({ type: 'schedule', data: { id: scheduleId, resources: { type, data: filteredFiles }}});
        } else {
            // Use targeted delete by id for asset/pdf
            dataDispatch({ type, data: { collection: 'files', deleteId: id } });
        };
    };

    const normalizeAndFilterFiles = ({ selectedFiles, currentFiles=[], maxLimit=5 }) => {
        // ******** NORMALIZE LOGO ********
        if (fileHandleType === 'logo') {
            const normalizedFiles = normalizeFilesOnUpload(selectedFiles);

            return normalizedFiles;
        // ******** NORMALIZE LOCATION ********
        } else if (fileHandleType === 'location') {
            if (selectedFiles?.length > 1) {
                throw new Error('too many files selected');
            };

            const file = selectedFiles[0];
            const newFile = new File([file], 'location_snapshot.webp', {
                metadata: file.metadata,
                type: file.type,
            });

            return [{
                file: newFile,
                id: Date.now(),
                name: 'location_snapshot.webp',
                url: URL.createObjectURL(newFile)
            }];
        // ******** NORMALIZE QR CONNECT FILES ********
        } else if (fileHandleType === 'qr_connect') {
            if (selectedFiles?.length > 1) {
                throw new Error('too many files selected');
            };

            const file = selectedFiles[0];
            const newFile = new File([file], 'qr_connect_header.webp', {
                metadata: file.metadata,
                type: file.type,
            });

            return [{
                file: newFile,
                id: Date.now(),
                name: 'qr_connect_header.webp',
                url: URL.createObjectURL(newFile)
            }];
        // ******** NORMALIZE QR FILES ********
        } else {
            if ((currentFiles.length + selectedFiles.length) > maxLimit) {
                setError('Maximum number of files hit (5)');
            };

            const newFilesWithInitialMetadata = normalizeFilesOnUpload(selectedFiles);
            const slicedArray = newFilesWithInitialMetadata.slice(0, (maxLimit - currentFiles.length))

            const existingIds = new Set(currentFiles.map(prevFile => prevFile.id));

            const newUniqueFilesForDisplay = slicedArray
                .filter(newFile => !existingIds.has(newFile.id));

            const combinedFilesForDisplay = currentFiles.concat(newUniqueFilesForDisplay);

            return { combinedFilesForDisplay, newUniqueFilesForDisplay };
        };
    };

    const prepareFilesForUpload = async ({ filesToProcess=[] }) => {
        const formData = new FormData();
        const motionFiles = [];

        for (const fileObject of filesToProcess) {
            const { file, id } = fileObject;
            const processFileResponse = await processFile(file, acceptableFileTypes, id, fileHandleType);

            const { isGif = false } = processFileResponse;
            if (isGif) {
                if (processFileResponse?.path?.includes(processFileResponse?.id) && processFileResponse?.name?.includes(processFileResponse?.id) && processFileResponse?.url?.includes('temp_files')) {
                    motionFiles.push({ path: processFileResponse?.path, url: processFileResponse?.url, id: processFileResponse?.id, name: processFileResponse?.name });
                };
            } else {
                for (const f of processFileResponse.files) {
                    formData.append('file', f, f.name);
                };
            };
        };
        return { formData, motionFiles };
    };

    const updateFileStateWithUploadedData = (currentFilesState, uploadedDataFromServer, motionFiles) => {
        const currentFilesList = currentFilesState.map((file) => {
            const foundFile = uploadedDataFromServer.find(f => file.id === f.id);
            const motionFile = motionFiles.find(f => f.id === file.id);

            if (file.url.startsWith('blob:')) URL.revokeObjectURL(file.url);
            
            if (foundFile) {
                return {
                    id: file.id,
                    name: file.name,
                    path: foundFile.path,
                    url: foundFile.url,
                };
            } else if (motionFile) {
                return {
                    id: file.id,
                    name: file.name,
                    path: motionFile.path,
                    url: motionFile.url
                };
            };

            return file;
        }) || [];
        if (motionFiles?.length > 0) {
            currentFilesList.concat(...motionFiles);
        };

        return currentFilesList;
    };

    const handleFileSelected = async (selectedFiles) => {
        if (!selectedFiles || selectedFiles?.length === 0) {
            return;
        };

        switch (fileHandleType) {
            case 'qr_files':
                return await handleQrFiles(selectedFiles);
            case 'logo':
                return await handleLogoFiles(selectedFiles);
            case 'location':
                return await handleLocationFiles(selectedFiles);
            case 'schedule':
                return await handleScheduleFiles(selectedFiles);
            case 'qr_connect':
                return await handleQrConnectFiles(selectedFiles);
            default:
                break;
        };
    };

    const handleQrFiles = async (selectedFiles) => {
        let newlyAddedFileIds;
        try {
            setError('');
            const maxLimit = 5;
            const currentFiles = qr_data.data?.[type]?.files || [];
            
            const { combinedFilesForDisplay, newUniqueFilesForDisplay } = normalizeAndFilterFiles({
                selectedFiles,
                currentFiles,
                maxLimit
            });

            dataDispatch({ type, data: { files: combinedFilesForDisplay } });

            const { formData, motionFiles } = await prepareFilesForUpload({
                filesToProcess: newUniqueFilesForDisplay
            });

            newlyAddedFileIds = newUniqueFilesForDisplay.map(file => file.id);

            const uploadedFilesData = await handleUploadFiles(formData);

            const updatedFilesState = updateFileStateWithUploadedData(
                combinedFilesForDisplay,
                uploadedFilesData,
                motionFiles
            );

            dataDispatch({ type, data: { files: updatedFilesState } });

            const successfullyUploadedFilePaths = uploadedFilesData.map(f => f.path) || [];
            
            if (motionFiles.length > 0) {
                for (const file of motionFiles) {
                    successfullyUploadedFilePaths.push(file.path);
                };
            };

            setFiles(prev => ({
                ...prev,
                qrFiles: [
                    ...new Set([
                        ...prev.qrFiles,
                        ...successfullyUploadedFilePaths
                    ])
                ]
            }));

        } catch (err) {
            console.error('Error uploading files', err);
            if (err.message === 'max files') {
                setError('Maximum number of files hit');
            } else {
                setError('An error occurred during file upload. Please try again.');
                dataDispatch(prevQrData => {
                    const currentFiles = prevQrData.data[type].files || [];
                    const filesToKeep = currentFiles.filter(file => !newlyAddedFileIds.includes(file.id));

                    const filesToRemove = currentFiles.filter(file => newlyAddedFileIds.includes(file.id));
                    
                    filesToRemove.forEach(file => {
                        if (file.url.startsWith('blob:')) URL.revokeObjectURL(file.url);
                    });

                    return {
                        ...prevQrData,
                        data: {
                            ...prevQrData.data,
                            [type]: {
                                ...prevQrData.data[type],
                                files: filesToKeep
                            }
                        }
                    };
                });
            };
        };
    };

    const handleLogoFiles = async (selectedFiles) => {
        try {
            const logoFiles = normalizeAndFilterFiles({ selectedFiles });

            optionsDispatch({ type: 'logo', options: logoFiles[0].url });
            setLogo(logoFiles[0].url);

            const { formData } = await prepareFilesForUpload({ filesToProcess: logoFiles });
            const filesResponse = await handleUploadFiles(formData);

            dataDispatch({ type: 'logo_files', data: filesResponse });
            optionsDispatch({ type: 'logo', options: filesResponse[0].url });
            setFiles(prev => ({
                ...prev,
                logo: filesResponse.map(file => file.path)
            }));
        } catch (err) {
            console.error('Error handling logo file: ', err);
        };
    };

    const handleQrConnectFiles = async (selectedFiles) => {
        try {
            const connectFiles = normalizeAndFilterFiles({ selectedFiles });

            const { formData } = await prepareFilesForUpload({ filesToProcess: connectFiles });
            formData.append('orgId', activeOrgId);
            const { data: url } = await API.storeConnectHeaderImage(qr_data.id, formData);

            setFiles(prev => ({
                ...prev,
                qrFiles: [
                    ...new Set([
                        ...prev.qrFiles,
                        `temp_files/${qr_data.id}/qr_connect_header.webp`
                    ])
                ]
            }));

            dataDispatch({ type: 'connect-header', data: url });
        } catch (err) {
            console.error('Error handling qr connect file: ', err);
        };
    };

    const handleLocationFiles = async (selectedFiles) => {
        try {
            const locationFiles = normalizeAndFilterFiles({ selectedFiles });
            const { formData } = await prepareFilesForUpload({ filesToProcess: locationFiles });
            const { data: url } = await API.storeSnapshot(qr_data.id, formData);

            dataDispatch({ type: 'location_snapshot', data: url});
        } catch (err) {
            console.error('Error handling location files: ', err);
        };
    };

    const handleScheduleFiles = async (selectedFiles) => {
        try {
            if (!scheduleId) {
                console.error(new Error('Missing Schedule ID'));
                return;
            };

            const currentFiles = qr_data.data?.schedule?.[scheduleId]?.resources?.data || [];

            const {
                combinedFilesForDisplay,
                newUniqueFilesForDisplay
            } = normalizeAndFilterFiles({
                selectedFiles,
                currentFiles
            });

            dataDispatch({ type: 'schedule', data: { id: scheduleId, resources: { type, data: combinedFilesForDisplay }}})
            const { formData, motionFiles } = await prepareFilesForUpload({ filesToProcess: newUniqueFilesForDisplay });
            const filesResponse = await handleUploadFiles(formData);
            const updatedFileState = updateFileStateWithUploadedData(combinedFilesForDisplay, filesResponse, motionFiles);
            dataDispatch({ type: 'schedule', data: { id: scheduleId, resources: { type, data: updatedFileState }}})
        
            const successfullyUploadedFiles = filesResponse.map(f => f.path) || [];

            if (motionFiles.length > 0) {
                for (const file of motionFiles) {
                    successfullyUploadedFiles.push((file.path));
                };
            };

            setFiles(prev => ({
                ...prev,
                scheduleFiles: {
                    scheduleId,
                    files: [
                    ...new Set([
                        ...(prev.scheduleFiles?.files || []),
                        ...successfullyUploadedFiles
                    ])
                ]}
            }));
        } catch (err) {
            console.error('Error handling schedule files: ', err);
        };
    };

    const onFileNameChange = (id, name) => {
        setError('');

        if (fileHandleType === 'schedule') {
            if (!scheduleId) {
                throw new Error('Missing scheduleId');
            }

            const updatedFiles = qr_data.data.schedule[scheduleId].resources.data.map(file => {
                if (file.id === id) {
                    file.name = name;
                };
                return file;
            });

            dataDispatch({ type, data: { id: scheduleId, resources: { type, data: updatedFiles }}});
        } else {
            dataDispatch({ type, data: { collection: 'files', id, field: 'name', value: name } });
        };
    };

    const handleUploadFiles = useCallback(async (selectedFiles) => {
        try {
            const { data } = await API.storeFiles(selectedFiles);
            return data;
        } catch (err) {
            console.error('Error uploading files at handleUploadFiles: ', err);
            setError(err.message);
        };
    }, []);

    return { onFileNameChange, handleFileSelected, onDeleteFile, filesToDisplay, error };
};
